import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { getHideWelcomePopup } from '@jsmdg/browser-storage';
import { Breakpoint, useBreakpoint } from '@jsmdg/yoshi';
import { getLoginUrl } from '../../../helper/customerAccountUrlUtils';
import { useActivateListItemWithDelay } from '../../../hooks/useActivateListItemWithDelay';
import { useCustomerContext } from '../../../hooks/useCustomerContext';
import { CustomerAccountButton } from '../CustomerAccountButton/CustomerAccountButton';
import styles from './CustomerAccountFlyout.module.scss';

type CustomerAccountFlyoutProps = {
    readonly setShowFlyout: (showFlyout: boolean) => void;
};

const LoadableLoggedOutCustomerFlyout = loadable(
    async () => import('../LoggedOutCustomerFlyout/LoggedOutCustomerFlyout'),
);

const LoadableLoggedInCustomerFlyout = loadable(
    async () => import('../LoggedInCustomerFlyout/LoggedInCustomerFlyout'),
);

const LoadableCustomerWelcomeFlyout = loadable(
    async () => import('../CustomerWelcomeFlyout/CustomerWelcomeFlyout'),
);

const CustomerAccountFlyout: React.FC<CustomerAccountFlyoutProps> = ({ setShowFlyout }) => {
    const { hasFetched, isLoggedIn, isNewCustomer } = useCustomerContext();

    useEffect(() => {
        if (hasFetched && getHideWelcomePopup().get() !== null && !isLoggedIn) {
            getHideWelcomePopup().remove();
        }
    }, [hasFetched, isLoggedIn]);

    const isNotSmallScreen = useBreakpoint(Breakpoint.XS);
    const isTabletScreen = useBreakpoint(Breakpoint.SM);
    const isDesktop = useBreakpoint(Breakpoint.MD);

    const [isWelcomeFlyoutDisabled, setIsWelcomeFlyoutDisabled] = useState(!isNewCustomer);

    useEffect(() => {
        setIsWelcomeFlyoutDisabled(!isNewCustomer);
    }, [isNewCustomer]);

    const { activeItemIndex, resetActiveItemIndexWithDelay, setActiveItemIndexWithDelay } =
        useActivateListItemWithDelay();
    const showCustomerAccountFlyout = activeItemIndex !== -1;
    const showCustomerWelcomeFlyout =
        !isWelcomeFlyoutDisabled && !getHideWelcomePopup().get() && isLoggedIn;

    const handleCustomerAccountButtonClick = (show: boolean): void => {
        if (isLoggedIn || !isNotSmallScreen) {
            window.location.assign(isLoggedIn ? '/account' : getLoginUrl());
            return;
        }

        if (show) {
            setActiveItemIndexWithDelay(1);
        } else {
            resetActiveItemIndexWithDelay();
        }
    };

    const handleOnClick = (): void => {
        if (isDesktop) handleCustomerAccountButtonClick(true);

        if (isLoggedIn) {
            window.location.href = '/account';
        } else {
            setShowFlyout(true);
            setActiveItemIndexWithDelay(1);
        }
    };

    const handleMouseEnter = (): void => {
        if (!isTabletScreen) return;
        setActiveItemIndexWithDelay(1);
    };

    const handleMouseLeave = (): void => {
        if (!isTabletScreen) return;
        resetActiveItemIndexWithDelay();
    };

    const renderFlyoutContent = (): JSX.Element | null => {
        if (!showCustomerAccountFlyout) return null;

        if (!isLoggedIn) {
            return (
                <LoadableLoggedOutCustomerFlyout
                    onMouseEnter={() => setActiveItemIndexWithDelay(1)}
                    onRequestClose={() => resetActiveItemIndexWithDelay()}
                    isTablet={isTabletScreen}
                />
            );
        }

        if (isDesktop) {
            return (
                <LoadableLoggedInCustomerFlyout
                    onMouseEnter={() => setActiveItemIndexWithDelay(1)}
                    onRequestClose={() => resetActiveItemIndexWithDelay()}
                />
            );
        } else {
            return null;
        }
    };

    return (
        <div className="position-relative ml-sm-1-5x">
            <CustomerAccountButton
                highlighted={showCustomerWelcomeFlyout}
                layoutClassName={classNames(styles.icon)}
                onClick={handleOnClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
            />
            {renderFlyoutContent()}

            {showCustomerWelcomeFlyout && (
                <LoadableCustomerWelcomeFlyout
                    onRequestClose={() => {
                        setIsWelcomeFlyoutDisabled(true);
                        getHideWelcomePopup().set('true');
                    }}
                />
            )}
        </div>
    );
};

export default CustomerAccountFlyout;
